import { z } from 'zod';

/** Today in the local timezone, as a date input holds it. */
export const getLocalDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * One driver, as both screens collect them. The admin dashboard has its own
 * copy of these rules in `newUserSchema`; they differ (this one takes no
 * truck on onboarding, and the admin one checks the start date's age), so
 * they are kept apart rather than shared and bent.
 */
export const driverRowSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name cannot exceed 100 characters'),
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  phone: z.string().optional(),
  status: z.enum(['ACTIVE', 'ON_LEAVE']).default('ACTIVE'),
  startDate: z.string().min(1, 'Start date is required'),
  assignedTruckId: z.string().optional(),
});

export type DriverRow = z.infer<typeof driverRowSchema>;

export const emptyDriverRow = (): DriverRow => ({
  name: '',
  email: '',
  phone: '',
  status: 'ACTIVE',
  startDate: getLocalDate(),
  assignedTruckId: '',
});

/** The single-driver screen. */
export const createDriverSchema = driverRowSchema;

/**
 * The onboarding screen, which collects a list.
 *
 * Every row is validated in full, so a half-filled row names its own missing
 * fields instead of the one toast the screen used to raise for all of them.
 */
export const createDriversSchema = z.object({
  drivers: z.array(driverRowSchema).min(1, 'Add at least one driver.'),
});

export type CreateDriversValues = z.infer<typeof createDriversSchema>;
